import type { WorkflowReducer } from "../../workflowReducer";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import makeStyles from "@mui/styles/makeStyles";
import Paper from "@mui/material/Paper";
import {
  TimelineSeparator,
  TimelineConnector,
  SpacerTimelineItem,
  TimelineOppositeContent,
  TimelineEndDot,
  TimelineContent,
} from "./StyledTimelineComponents";
import Timeline from "@mui/lab/Timeline";
import { timelineItemClasses } from "@mui/lab/TimelineItem";
import { MeasurementsOrderedTimelineItem } from "./MeasurementsOrderedTimelineItem";

const useStyles = makeStyles((theme) => ({
  measurementsContainer: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(2),
  },
  droppablePanel: {
    width: "100%",
  },
  emptyPlaceholder: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface Props {
  workflowReducer: WorkflowReducer;
  editMode?: boolean;
}

export function MeasurementsOrderedTimeline(props: Props) {
  const classes = useStyles();
  const isEditMode = props.editMode ?? false;

  const orderedMeasurementsCount = props.workflowReducer.state.orderedMeasurements.length;

  const onDragEnd = (dropResult: DropResult) => {
    if (!isEditMode) {
      return;
    }

    const { destination, source } = dropResult;

    if (destination && source && destination.index !== source.index) {
      props.workflowReducer.reorderOrderedMeasurements(source.index, destination.index);
    }
  };

  return (
    <Paper variant="outlined" className={classes.measurementsContainer}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list" isDropDisabled={!isEditMode}>
          {(droppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              className={classes.droppablePanel}
            >
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {props.workflowReducer.state.orderedMeasurements
                  ?.slice()
                  .sort((a, b) => a.order - b.order)
                  ?.map((measurement, index) => {
                    const isFirstItemInTimeline = index === 0;
                    const isLastItemInTimeline = index === orderedMeasurementsCount - 1;
                    const measurementId = [
                      "measure",
                      measurement.parentAssetId,
                      measurement.panelAssetId,
                      measurement.panelNo,
                      measurement.type,
                      measurement.accessory,
                      measurement.access_location,
                      measurement.component,
                      measurement.subloc,
                    ].join("-");

                    return (
                      <Draggable
                        key={measurementId}
                        draggableId={measurementId}
                        index={index}
                        isDragDisabled={!isEditMode}
                      >
                        {(draggableProvided) => (
                          <span
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...(isEditMode ? draggableProvided.dragHandleProps : {})}
                          >
                            <MeasurementsOrderedTimelineItem
                              workflowReducer={props.workflowReducer}
                              measurement={measurement}
                              dragHandleProps={isEditMode ? draggableProvided.dragHandleProps : undefined}
                              editMode={props.editMode}
                              isFirstItemInTimeline={isFirstItemInTimeline}
                            />
                            <SpacerTimelineItem>
                              <TimelineOppositeContent />
                              <TimelineSeparator>
                                <TimelineConnector />
                                {isLastItemInTimeline && <TimelineEndDot />}
                              </TimelineSeparator>
                              <TimelineContent />
                            </SpacerTimelineItem>
                          </span>
                        )}
                      </Draggable>
                    );
                  })}
                {droppableProvided.placeholder}
              </Timeline>
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );
}
